import { Ant, Position } from '../Ant';
import { TunnelNetwork, Chamber } from '../TunnelNetwork';
import { EventBus, GameEventTypes } from '../../EventBus';
import { Rng } from '../../Rng';

/**
 * Debris particle for visual damage effects.
 */
export interface DebrisParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  life: number;
  maxLife: number;
}

/**
 * Pending damage accumulator for merged logging.
 */
export interface PendingDamage {
  tunnels: number;
  chambers: number;
  deaths: number;
  injuries: number;
  lastEventTime: number;
  source: string;
}

/**
 * Context required for damage operations.
 */
export interface DamageContext {
  tunnelNetwork: TunnelNetwork;
  ants: Map<number, Ant>;
  surfacePortalX: number;
  surfacePortalY: number;
  gameTimeMs: number;
  layerId: string;
  addNews: (msg: string, type: string) => void;
  killAnt: (ant: Ant, cause: string) => void;
  setAlertState: (alert: boolean, timer: number) => void;
  events: EventBus;
}

/**
 * Configuration for the damage system.
 */
export interface DamageConfig {
  CELL_TO_COLONY_X: number;
  CELL_TO_COLONY_Y: number;
  DAMAGE_MERGE_WINDOW: number;
  DAMAGE_RADIUS: number;
}

export const DEFAULT_DAMAGE_CONFIG: DamageConfig = {
  CELL_TO_COLONY_X: 20,    // 800/40
  CELL_TO_COLONY_Y: 13.3,  // 400/30
  DAMAGE_MERGE_WINDOW: 100, // Merge events within 100ms
  DAMAGE_RADIUS: 8,        // Only damage if construction within 8 cells of portal
};

/**
 * DamageSystem handles all construction-related damage to the colony.
 * Manages vibrations, excavations, concrete pours, and debris particles.
 */
export class DamageSystem {
  private config: DamageConfig;
  private pendingDamage: PendingDamage = {
    tunnels: 0,
    chambers: 0,
    deaths: 0,
    injuries: 0,
    lastEventTime: 0,
    source: ''
  };
  private debrisParticles: DebrisParticle[] = [];

  /** Decisions that change colony state: who dies, who is merely injured. */
  private rng: Rng;

  /**
   * Purely visual debris. Kept on a separate stream so a headless run that
   * skips particle spawning entirely still kills exactly the same ants.
   */
  private cosmeticRng: Rng;

  constructor(rng: Rng, config: Partial<DamageConfig> = {}) {
    this.rng = rng;
    this.cosmeticRng = rng.fork('debris');
    this.config = { ...DEFAULT_DAMAGE_CONFIG, ...config };
  }

  /**
   * The damage accumulated but not yet reported.
   *
   * Construction events inside the merge window are summed so the colony reads
   * one headline rather than forty, which makes the accumulator state: a snapshot
   * taken mid-window and restored without it would either lose the report or
   * start a second one. Debris particles are not here — cosmetic, drawn from a
   * forked stream, and absent from a headless run entirely.
   */
  save(): PendingDamage {
    return { ...this.pendingDamage };
  }

  load(state: PendingDamage): void {
    this.pendingDamage = { ...state };
    this.debrisParticles.length = 0;
  }

  /**
   * Map surface grid offset (relative to portal) to colony coordinates.
   */
  mapSurfaceOffsetToColony(
    surfaceX: number,
    surfaceY: number,
    ctx: DamageContext
  ): Position {
    const entrances = ctx.tunnelNetwork.getEntrances();
    if (entrances.length === 0) {
      return { x: 400, y: 50 }; // Fallback to center-top
    }

    const entrance = entrances[0];
    const offsetX = surfaceX - ctx.surfacePortalX;
    const offsetY = surfaceY - ctx.surfacePortalY;

    return {
      x: entrance.position.x + offsetX * this.config.CELL_TO_COLONY_X,
      y: entrance.position.y + Math.abs(offsetY) * this.config.CELL_TO_COLONY_Y * 0.5
    };
  }

  /**
   * Check if a surface position is near the colony's portal.
   */
  isNearPortal(surfaceX: number, surfaceY: number, ctx: DamageContext): boolean {
    if (ctx.surfacePortalX < 0 || ctx.surfacePortalY < 0) return false;
    const dx = surfaceX - ctx.surfacePortalX;
    const dy = surfaceY - ctx.surfacePortalY;
    return Math.sqrt(dx * dx + dy * dy) <= this.config.DAMAGE_RADIUS;
  }

  /**
   * Accumulate damage and flush to log when events stop coming.
   */
  accumulateDamage(
    tunnels: number,
    chambers: number,
    deaths: number,
    injuries: number,
    source: string,
    impactX: number,
    impactY: number,
    ctx: DamageContext
  ): void {
    const now = ctx.gameTimeMs;

    // If outside merge window, flush previous damage first
    if (now - this.pendingDamage.lastEventTime > this.config.DAMAGE_MERGE_WINDOW &&
        (this.pendingDamage.tunnels > 0 || this.pendingDamage.chambers > 0)) {
      this.flushDamageLog(ctx);
    }

    // Accumulate new damage
    this.pendingDamage.tunnels += tunnels;
    this.pendingDamage.chambers += chambers;
    this.pendingDamage.deaths += deaths;
    this.pendingDamage.injuries += injuries;
    this.pendingDamage.lastEventTime = now;
    this.pendingDamage.source = source;

    // Spawn debris particles at impact location
    if (tunnels > 0 || chambers > 0) {
      this.spawnDebris(impactX, impactY, tunnels + chambers * 3);
    }
  }

  /**
   * Flush accumulated damage to the news log.
   */
  flushDamageLog(ctx: DamageContext): void {
    const d = this.pendingDamage;
    if (d.tunnels === 0 && d.chambers === 0) return;

    // Build merged damage message
    const parts: string[] = [];
    if (d.chambers > 0) parts.push(`${d.chambers} chamber${d.chambers > 1 ? 's' : ''} destroyed`);
    if (d.tunnels > 0) parts.push(`${d.tunnels} tunnel${d.tunnels > 1 ? 's' : ''} collapsed`);

    let message = `DAMAGE [${d.source}]: ${parts.join(', ')}`;

    if (d.deaths > 0 || d.injuries > 0) {
      const casualtyParts: string[] = [];
      if (d.deaths > 0) casualtyParts.push(`${d.deaths} dead`);
      if (d.injuries > 0) casualtyParts.push(`${d.injuries} injured`);
      message += ` - ${casualtyParts.join(', ')}`;
    }

    ctx.addNews(message, 'alert');

    // Check for critical damage
    const nursery = ctx.tunnelNetwork.getNursery();
    if (nursery?.destroyed) {
      ctx.addNews('CRITICAL: Nursery chamber destroyed!', 'alert');
    }
    const queenChamber = ctx.tunnelNetwork.getQueenChamber();
    if (queenChamber?.destroyed) {
      ctx.addNews('CRITICAL: Queen chamber destroyed!', 'alert');
    }

    // Reset accumulator
    this.pendingDamage = {
      tunnels: 0,
      chambers: 0,
      deaths: 0,
      injuries: 0,
      lastEventTime: 0,
      source: ''
    };
  }

  /**
   * Spawn debris particles at a location.
   */
  spawnDebris(x: number, y: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = this.cosmeticRng.next() * Math.PI * 2;
      const speed = 20 + this.cosmeticRng.next() * 40;
      this.debrisParticles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 30, // Upward bias
        size: 2 + this.cosmeticRng.next() * 4,
        life: 1500 + this.cosmeticRng.next() * 1000, // 1.5-2.5 seconds
        maxLife: 2500
      });
    }
  }

  /**
   * Update debris particles and check for pending damage flush.
   */
  update(dt: number, ctx: DamageContext): void {
    // Update debris particles
    for (let i = this.debrisParticles.length - 1; i >= 0; i--) {
      const p = this.debrisParticles[i];
      p.x += p.vx * dt / 1000;
      p.y += p.vy * dt / 1000;
      p.vy += 80 * dt / 1000; // Gravity
      p.vx *= 0.98; // Drag
      p.life -= dt;

      if (p.life <= 0) {
        this.debrisParticles.splice(i, 1);
      }
    }

    // Check if we need to flush damage log
    if (ctx.gameTimeMs - this.pendingDamage.lastEventTime > this.config.DAMAGE_MERGE_WINDOW &&
        (this.pendingDamage.tunnels > 0 || this.pendingDamage.chambers > 0)) {
      this.flushDamageLog(ctx);
    }
  }

  /**
   * Handle vibration event from surface construction.
   */
  handleVibration(payload: unknown, ctx: DamageContext): void {
    const data = payload as { x: number; y: number; intensity: number };

    // Only affect this colony if construction is near its portal
    if (!this.isNearPortal(data.x, data.y, ctx)) return;

    // Set alert state
    ctx.setAlertState(true, 3000);
    ctx.events.emit(GameEventTypes.COLONY_ALERT, { type: 'vibration' }, ctx.layerId);

    // Map surface position to colony coordinates
    const { x: colonyX, y: colonyY } = this.mapSurfaceOffsetToColony(data.x, data.y, ctx);

    // Vibrations cause very minor damage only at shallow depths
    const damage = ctx.tunnelNetwork.collapseArea(
      { x: colonyX, y: colonyY },
      this.config.CELL_TO_COLONY_X * 0.25,
      this.config.CELL_TO_COLONY_X * 0.75,
      0.15
    );

    if (damage.tunnels > 0 || damage.chambers > 0) {
      this.accumulateDamage(damage.tunnels, damage.chambers, 0, 0, 'Vibration', colonyX, colonyY, ctx);
    }
  }

  /**
   * Handle excavation event from surface construction.
   */
  handleExcavation(payload: unknown, ctx: DamageContext): void {
    const data = payload as { x: number; y: number };

    // Only affect this colony if construction is near its portal
    if (!this.isNearPortal(data.x, data.y, ctx)) return;

    // Map surface position to colony coordinates
    const { x: colonyX, y: colonyY } = this.mapSurfaceOffsetToColony(data.x, data.y, ctx);

    const damage = ctx.tunnelNetwork.collapseArea(
      { x: colonyX, y: colonyY },
      this.config.CELL_TO_COLONY_X * 0.5,
      this.config.CELL_TO_COLONY_X * 1.5,
      0.4
    );

    let deaths = 0;
    let injuries = 0;

    // Kill or injure ants in collapsed areas
    for (const antId of damage.antsAffected) {
      const ant = ctx.ants.get(antId);
      if (ant) {
        if (this.rng.next() > 0.7) {
          ctx.killAnt(ant, 'tunnel collapse');
          deaths++;
        } else {
          ant.health -= 30;
          ant.injured = true;
          injuries++;
        }
      }
    }

    if (damage.tunnels > 0 || damage.chambers > 0) {
      this.accumulateDamage(damage.tunnels, damage.chambers, deaths, injuries, 'Excavation', colonyX, colonyY, ctx);
    }
  }

  /**
   * Handle concrete pour event from surface construction.
   */
  handleConcretePour(payload: unknown, ctx: DamageContext): void {
    const data = payload as { x: number; y: number };

    // Only affect this colony if construction is near its portal
    if (!this.isNearPortal(data.x, data.y, ctx)) return;

    // Map surface position to colony coordinates
    const { x: colonyX, y: colonyY } = this.mapSurfaceOffsetToColony(data.x, data.y, ctx);

    const damage = ctx.tunnelNetwork.collapseArea(
      { x: colonyX, y: colonyY },
      this.config.CELL_TO_COLONY_X * 0.75,
      this.config.CELL_TO_COLONY_X * 2,
      0.6
    );

    let deaths = 0;
    let injuries = 0;

    // Higher lethality for concrete pour
    for (const antId of damage.antsAffected) {
      const ant = ctx.ants.get(antId);
      if (ant) {
        if (this.rng.next() > 0.5) {
          ctx.killAnt(ant, 'tunnel collapse');
          deaths++;
        } else {
          ant.health -= 50;
          ant.injured = true;
          injuries++;
        }
      }
    }

    if (damage.tunnels > 0 || damage.chambers > 0) {
      this.accumulateDamage(damage.tunnels, damage.chambers, deaths, injuries, 'Concrete', colonyX, colonyY, ctx);
    }
  }

  /**
   * Get all debris particles for rendering.
   */
  getDebrisParticles(): DebrisParticle[] {
    return this.debrisParticles;
  }

  /**
   * Get current config.
   */
  getConfig(): DamageConfig {
    return { ...this.config };
  }

  /**
   * Update config with partial values (for live settings changes).
   */
  updateConfig(partial: Partial<DamageConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  /**
   * Reset the damage system state.
   */
  reset(): void {
    this.pendingDamage = {
      tunnels: 0,
      chambers: 0,
      deaths: 0,
      injuries: 0,
      lastEventTime: 0,
      source: ''
    };
    this.debrisParticles = [];
  }
}
